import Button from "app/components/button/button";
import CircleLoader from "app/components/loader/CircleLoader";
import { Path } from "app/constants/paths";
import useAppNavigation from "app/hooks/useAppNavigate";
import { useTheme } from "app/shared";
import closeModalIcon from "assets/img/icons/closeModal.svg";
import { FC, useEffect, useState } from "react";
import { FaXTwitter } from "react-icons/fa6";
import { LuCopy, LuCopyCheck } from "react-icons/lu";
import { RiAiGenerate } from "react-icons/ri";
import Modal from "react-modal";
import { useParams } from "react-router-dom";
import {
  useGetReferralFullLinkQuery,
  useLazyGetRefTextQuery,
} from "store/api/new.api";
import { useAccount } from "wagmi";
import styles from "./RefModal.module.scss";

// Set the app element for react-modal accessibility
if (typeof document !== "undefined") {
  Modal.setAppElement("#root");
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  canGenerateText: boolean;
  onUpdateReferralLink: (link: string) => void;
  isPreSale: boolean;
}

const RefModal: FC<ModalProps> = ({
  isOpen,
  onClose,
  canGenerateText,
  onUpdateReferralLink,
  isPreSale,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [referralLink, setReferralLink] = useState<string>("");
  const { theme } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { address } = useAccount();
  const { navigateToBuildKarma } = useAppNavigation();
  const { idoId } = useParams<{ idoId: string }>();
  const [params, setParams] = useState<{
    idoId: string;
    address: string;
    name: string;
    refPercent: number;
  } | null>(null);

  useEffect(() => {
    if (idoId && address) {
      setParams({
        idoId,
        address,
        name: "",
        refPercent: 10,
      });
    }
  }, [idoId, address]);

  const {
    data: ref,
    error: refError,
    isLoading: refIsLoading,
  } = useGetReferralFullLinkQuery(params!, { skip: !params });

  const [
    triggerFetch,
    {
      data: generatedText,
      error: generatedTextError,
      isLoading: isGeneratedTextLoading,
    },
  ] = useLazyGetRefTextQuery();

  useEffect(() => {
    if (isOpen && canGenerateText) {
      triggerFetch({ idoId });
    }
  }, [isOpen, canGenerateText, idoId, triggerFetch]);

  useEffect(() => {
    if (ref?.refLink) {
      const generatedLink = ref.refLink;
      setReferralLink(generatedLink);
      onUpdateReferralLink(generatedLink);
    }
  }, [ref, idoId, onUpdateReferralLink]);

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      })
      .catch((err) => console.error("Failed to copy link:", err));
  };

  const handleRedirect = () => {
    // Preserve invite/utm params via centralized navigation helper
    navigateToBuildKarma();
  };

  const handleShareOnX = () => {
    const encodedText = encodeURIComponent(
      `${generatedText[currentIndex]}\n\n${referralLink}`
    );
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
    window.open(twitterUrl, "_blank", "noopener,noreferrer");
  };

  const handleRegenerate = () => {
    if (generatedText && generatedText.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % generatedText.length);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={`${styles.modalContent} ${
        theme === "dark" ? styles.darkTheme : ""
      }`}
      overlayClassName={styles.modalOverlay}
      ariaHideApp={true}
    >
      <button className={styles.closeButton} onClick={onClose}>
        <img src={closeModalIcon} alt="close modal button" />
      </button>
      {isPreSale ? (
        <>
          <h2>🎉 Congratulations! You’ve Secured Your Allocation! 🎉</h2>
          <p>
            You’re all set for the IDO and will receive <b>4,000 Karma</b> at
            launch! 🚀
          </p>
          <p>
            Karma is your key to unlocking exclusive perks, rewards, and future
            opportunities in the ecosystem.
          </p>
        </>
      ) : (
        <>
          <h2>🎉 Congratulations!🎉</h2>
          <p>
            You’ve got <b>4,000 Karma</b>! 🚀
          </p>
        </>
      )}
      <Button
        variant="pink"
        onClick={handleRedirect}
        className={styles.getMoreBtn}
      >
        Get More Karma
      </Button>
      {canGenerateText && (
        <>
          <p className={styles.assistantText}>
            AlphaMind's <b>AI-assistant</b> crafted a message for you to share
            and maximize your impact. Use the one below (or regenerate), share
            on X and earn $ rewards from each referral!
          </p>
          <div
            className={`${styles.messageBox} ${
              theme === "dark" ? styles.darkTheme : ""
            }`}
          >
            {isGeneratedTextLoading && (
              <CircleLoader
                className={styles.loadear}
                colorType={theme === "dark" ? "light" : "dark"}
                isLoading={isGeneratedTextLoading}
              />
            )}
            {!isGeneratedTextLoading && generatedTextError && (
              <span>Ooops... Something went wrong, please try again</span>
            )}
            {!isGeneratedTextLoading &&
              !generatedTextError &&
              generatedText?.[currentIndex] && (
                <p>{generatedText[currentIndex]}</p>
              )}
            {!isGeneratedTextLoading &&
              !generatedTextError &&
              !generatedText?.[currentIndex] && (
                <span>No message available.</span>
              )}
          </div>
        </>
      )}
      <div className={styles.btnWrapper}>
        <Button
          variant="pink"
          onClick={handleCopyLink}
          className={styles.copyButton}
        >
          {isCopied ? (
            <div className={styles.copied}>
              Link is Copied <LuCopyCheck />
            </div>
          ) : (
            <div>
              Copy my Link <LuCopy />
            </div>
          )}
        </Button>
        {canGenerateText && (
          <>
            <Button
              disabled={isGeneratedTextLoading}
              className={styles.shareButton}
              onClick={handleShareOnX}
            >
              Share on <FaXTwitter />
            </Button>
            <Button
              disabled={isGeneratedTextLoading}
              variant="pink"
              className={styles.regenerateButton}
              onClick={handleRegenerate}
            >
              Regenerate <RiAiGenerate />
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default RefModal;
